import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';

import { ChartComponent } from '@next/core-lib/chart';
import { PrintPageComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import * as Highcharts from 'highcharts';
import { skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { PrintService } from 'src/app/shared/services/print.service';
import Utils from 'src/app/utils';

import { Parameter, WeeklyKpiDataResult } from '../../../performance/models/performance.model';
import * as performanceActions from '../../../performance/store/actions/performance.actions';

import { getWeeklyKpiDataResults } from '../../../performance/store/selectors/performance.selector';
import * as refvalsheetActions from '../../../reference-value-sheet/store/reference-value-sheet.actions';
import { Barn } from '../../../settings/models/barn.model';
import { Cycle, PropagationCompany } from '../../models/cycle.model';

import {
  getArrivals,
  getPropagationCompanies,
  getSelectedCycle,
} from '../../store/selectors/cycle.selectors';

export interface CycleReportInfo {
  sectionId: number;
  location: string;
  breed: string;
  gender: string;
  chickBirthDate: string;
  barnName: string;
  sectionName: string;
  feedSupply: string;
  waterSupply: string;
  propagationCompanyName: string;
  weekData?: CycleReportWeekData[];
  chartSeries?: Highcharts.SeriesOptionsType[];
}
export interface CycleReportWeekData {
  weeknr: number;
  numberOfAnimals: number;
  animalWeight: number;
  animalWeightReference: number;
  uniformity: number;
  feedintake: number;
  feedintakeReference: number;
  waterfeedRatio: number;
  numberOfHoursLight: number;
  lightIntensity: number;
  mortalityPercentageDeath: number;
  mortalityPercentageSelection: number;
  mortalityPercentageSexError: number;
  cumulativeMortalityPercentage: number;
}

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  imports: [
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    MatButton,
    PrintPageComponent,
    ChartComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class ReportsComponent {
  readonly propagationCompanies$ = this.store.select(getPropagationCompanies);

  selectedPropagationCompany: PropagationCompany;
  sectionIds: number[];
  cycleReport: CycleReportInfo[];
  chartOptions: Highcharts.Options;

  constructor(
    private readonly store: Store<RootState>,
    private readonly cdref: ChangeDetectorRef,
    private readonly localeService: LocaleService,
    private readonly printService: PrintService,
  ) {}

  private getInitialChartOptions(): Highcharts.Options {
    return {
      chart: {
        height: 600,
        width: 1000,
      },
      xAxis: {
        title: {
          enabled: true,
          text: this.localeService.translate('reports.table.weeknr'),
        },
      },
      yAxis: {
        title: {
          enabled: true,
          text: this.localeService.translate('reports.table.animalweight_unit'),
        },
      },
      tooltip: { enabled: false },
      plotOptions: {
        line: {
          animation: false,
          connectNulls: true,
        },
        series: {
          connectNulls: true,
        },
      },
      credits: {
        enabled: false,
      },
    } as Highcharts.Options;
  }

  selectPropagationCompany() {
    this.store
      .select(getSelectedCycle)
      .pipe(
        withLatestFrom(this.store.select(getArrivals)),
        skipWhile((cycle, _) => cycle === null || cycle === undefined),
        take(1),
      )
      .subscribe(([cycle, arrivals]) => {
        this.sectionIds = [];
        arrivals.forEach((arrival) => {
          arrival.allocations.forEach((allocation) => {
            if (
              allocation.propagationCompany.id === this.selectedPropagationCompany.id &&
              this.sectionIds.findIndex((id) => id === allocation.section.id) === -1
            ) {
              this.sectionIds.push(allocation.section.id);
            }
          });
        });

        cycle.movements.forEach((movement) => {
          if (
            movement.toPropagationCompany.id === this.selectedPropagationCompany.id &&
            this.sectionIds.findIndex((row) => row === movement.toSection.id) === -1
          ) {
            this.sectionIds.push(movement.toSection.id);
          }
        });

        this.store.dispatch(
          performanceActions.loadWeeklyKpiResultsRequest({
            cycleId: cycle.id,
            weeklyKpiDataRequest: {
              parameters: [
                Parameter.NumberOfAnimalsAlive,
                Parameter.AnimalWeightInGrams,
                Parameter.Uniformity,
                Parameter.FeedIntakeInGramsPerAnimalPerDay,
                Parameter.WaterFeedRatio,
                Parameter.NumberOfHoursLight,
                Parameter.LightIntensityInLux,
                Parameter.MortalityPercentageDeath,
                Parameter.MortalityPercentageSelection,
                Parameter.MortalityPercentageSexError,
                Parameter.CumulativeMortalityPercentage,
                Parameter.FeedIntakeNormReference,
                Parameter.ArrivalAgeInDays,
                Parameter.AnimalWeightNormReference,
              ],
              sectionIds: this.sectionIds,
            },
          }),
        );
        this.store.dispatch(
          refvalsheetActions.loadRefValSheetKPIsRequest({
            ids: cycle.sections
              .filter((s) => s.referenceValueSheetId !== null)
              .map((s) => s.referenceValueSheetId),
          }),
        );
      });
  }

  getParamValue(week: WeeklyKpiDataResult, param: string) {
    const foundValue = week.data.find((d) => d.parameter.name === param).value;
    if (typeof foundValue !== 'number') {
      return null;
    }
    return foundValue;
  }

  getDifferenceBirthDateArrivalDateForSection(cycle: Cycle, sectionId: number): number {
    let earliestSectionBirthDate: Date;
    let earliestSectionArrivalDate: Date;
    let difference = 0;
    cycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        if (
          allocation.section.id === sectionId &&
          allocation.propagationCompany.id === this.selectedPropagationCompany.id
        ) {
          const chickBirthDate = Utils.toUtcDate(new Date(arrival.chickBirthDate));
          const chickArrivalDate = Utils.toUtcDate(new Date(arrival.arrivalDate));
          if (earliestSectionBirthDate === undefined || chickBirthDate < earliestSectionBirthDate) {
            earliestSectionBirthDate = chickBirthDate;
            earliestSectionArrivalDate = chickArrivalDate;
          }
        }
      });
    });

    if (earliestSectionBirthDate && earliestSectionBirthDate) {
      difference =
        (earliestSectionArrivalDate.getTime() - earliestSectionBirthDate.getTime()) /
        (1000 * 3600 * 24);
    }

    return difference;
  }

  print(): void {
    this.store
      .select(getSelectedCycle)
      .pipe(
        withLatestFrom(
          this.store.select(getWeeklyKpiDataResults),
          this.store.select(UserSelectors.selectLocation),
        ),
        skipWhile(([cycle, _, __]) => cycle === null || cycle === undefined),
        take(1),
      )
      .subscribe(([cycle, weeklyKpiData, location]) => {
        this.cycleReport = [];
        this.chartOptions = this.getInitialChartOptions();

        if (cycle) {
          this.sectionIds
            .slice()
            .sort((a, b) => a - b)
            .forEach((sectionId) => {
              const weekData: CycleReportWeekData[] = [];
              const chartWeekData_animalweight: { x: number; y: number }[] = [];
              const chartWeekData_animalweightReference: { x: number; y: number }[] = [];

              weeklyKpiData
                .filter((week) => week.sectionId === sectionId)
                .forEach((week) => {
                  const weekNumberFromBirth =
                    Math.floor(this.getParamValue(week, Parameter.ArrivalAgeInDays) / 7) +
                    week.weekNr;

                  // graph data
                  chartWeekData_animalweight.push({
                    x: weekNumberFromBirth,
                    y: this.getParamValue(week, Parameter.AnimalWeightInGrams),
                  });
                  chartWeekData_animalweightReference.push({
                    x: weekNumberFromBirth,
                    y: this.getParamValue(week, Parameter.AnimalWeightNormReference),
                  });

                  // table data
                  weekData.push({
                    weeknr: weekNumberFromBirth,
                    numberOfAnimals: this.getParamValue(week, Parameter.NumberOfAnimalsAlive),
                    animalWeight: this.getParamValue(week, Parameter.AnimalWeightInGrams),
                    animalWeightReference: this.getParamValue(
                      week,
                      Parameter.AnimalWeightNormReference,
                    ),
                    uniformity: this.getParamValue(week, Parameter.Uniformity),
                    feedintake: this.getParamValue(
                      week,
                      Parameter.FeedIntakeInGramsPerAnimalPerDay,
                    ),
                    feedintakeReference: this.getParamValue(
                      week,
                      Parameter.FeedIntakeNormReference,
                    ),
                    waterfeedRatio: this.getParamValue(week, Parameter.WaterFeedRatio),
                    numberOfHoursLight: this.getParamValue(week, Parameter.NumberOfHoursLight),
                    lightIntensity: this.getParamValue(week, Parameter.LightIntensityInLux),
                    mortalityPercentageDeath: this.getParamValue(
                      week,
                      Parameter.MortalityPercentageDeath,
                    ),
                    mortalityPercentageSelection: this.getParamValue(
                      week,
                      Parameter.MortalityPercentageSelection,
                    ),
                    mortalityPercentageSexError: this.getParamValue(
                      week,
                      Parameter.MortalityPercentageSexError,
                    ),
                    cumulativeMortalityPercentage: this.getParamValue(
                      week,
                      Parameter.CumulativeMortalityPercentage,
                    ),
                  } as CycleReportWeekData);
                });

              let selectedArrival = this.getArrival(
                cycle,
                this.selectedPropagationCompany.id,
                sectionId,
              );

              if (!selectedArrival) {
                const movement = cycle.movements.find(
                  (m) =>
                    m.toPropagationCompany.id === this.selectedPropagationCompany.id &&
                    m.toSection.id === sectionId,
                );

                selectedArrival = this.getArrival(
                  cycle,
                  movement.fromPropagationCompany.id,
                  movement.fromSection.id,
                );
              }

              const barn = <Barn>cycle.sections.find((s) => s.id === sectionId).barn;
              const sectionReport = {
                sectionId,
                location: `${location?.name}, ${location?.city}`,
                breed: cycle.sections.find((s) => s.id === sectionId).breed.name,
                gender: cycle.sections.find((s) => s.id === sectionId).gender,
                chickBirthDate: selectedArrival?.chickBirthDate,
                barnName: barn.name,
                sectionName: cycle.sections.find((s) => s.id === sectionId).name,
                feedSupply: barn.properties.methodOfFeedAdministration,
                waterSupply: barn.properties.waterSupply,
                propagationCompanyName: this.selectedPropagationCompany.name,
                weekData,
                chartSeries: [
                  {
                    type: 'line',
                    name: this.localeService.translate('reports.table.koppel'),
                    data: chartWeekData_animalweight,
                  },
                  {
                    type: 'line',
                    name: this.localeService.translate('reports.table.norm'),
                    data: chartWeekData_animalweightReference,
                  },
                ],
              } as CycleReportInfo;

              this.cycleReport.push(sectionReport);
            });
          this.cdref.detectChanges();

          this.printService.print({
            properties: {
              title: this.localeService.translate('reports.print-title'),
            },
          });
        }
      });
  }

  private getArrival(cycle: Cycle, propagationCompanyId: number, sectionId: number) {
    return cycle?.arrivals?.find((a) =>
      a.allocations.find(
        (all) => all.propagationCompany.id === propagationCompanyId && all.section.id === sectionId,
      ),
    );
  }
}
